'use client';

import { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSection {
  title: string;
  icon: string;
  items: FAQItem[];
}

const FAQ_SECTIONS: FAQSection[] = [
  {
    title: 'Marketplace',
    icon: 'ShoppingBagIcon',
    items: [
      { question: 'What is ZyForge?', answer: 'ZyForge is a premium marketplace for website templates across platforms like Shopify, WordPress, WooCommerce, and HTML/CSS. We offer high-quality, ready-to-use templates to help you launch faster.' },
      { question: 'Do I need an account to browse templates?', answer: 'No, you can browse and view templates without an account. However, you will need to create an account to purchase templates.' },
      { question: 'What platforms are supported?', answer: 'We currently support Shopify, WordPress, WooCommerce, and HTML/CSS templates. More platforms are added regularly.' },
      { question: 'How do I search for templates?', answer: 'Use the search bar at the top of the Templates page to search by name, category, or platform. You can also filter by price range and rating.' },
    ],
  },
  {
    title: 'Template Usage',
    icon: 'DocumentTextIcon',
    items: [
      { question: 'How do I install a template?', answer: 'After purchase, you will receive download links and installation instructions specific to your platform. Detailed guides are included with every template.' },
      { question: 'Can I customize the templates?', answer: 'Yes! All templates are fully customizable. You can modify colors, fonts, layouts, and content to match your brand. For advanced customization tips, visit the Community.' },
      { question: 'Are templates mobile-responsive?', answer: 'Yes, all ZyForge templates are fully responsive and optimized for mobile, tablet, and desktop devices.' },
      { question: 'Do templates receive updates?', answer: 'Yes, templates receive updates for compatibility and improvements. You can re-download updated versions from your account dashboard.' },
    ],
  },
  {
    title: 'Licensing',
    icon: 'KeyIcon',
    items: [
      { question: 'What license do I get with a template?', answer: 'Each template purchase includes a single-site license. You may use the template on one website. For multiple sites, you need to purchase additional licenses.' },
      { question: 'Can I use a template for a client project?', answer: 'Yes, you can use templates for client projects. Each purchase covers one end-use project. You cannot resell or redistribute the template files.' },
      { question: 'Can I resell ZyForge templates?', answer: 'No. Reselling, redistributing, or sublicensing ZyForge templates is strictly prohibited. Each license is for a single end-use project.' },
      { question: 'What happens if I violate the license?', answer: 'License violations may result in account suspension and legal action. Please review our Terms of Service for full details.' },
    ],
  },
  {
    title: 'Purchasing',
    icon: 'CreditCardIcon',
    items: [
      { question: 'What payment methods are accepted?', answer: 'We accept all major credit cards (Visa, Mastercard, American Express) and debit cards through our secure Stripe payment processor.' },
      { question: 'Is my payment information secure?', answer: 'Yes. All payments are processed securely through Stripe. ZyForge never stores your full card details.' },
      { question: 'Will I receive a receipt?', answer: 'Yes, a receipt is automatically sent to your email address after a successful purchase.' },
      { question: 'Can I use a discount code?', answer: 'Yes! Enter your discount code at checkout. Codes may have expiration dates or usage limits.' },
    ],
  },
  {
    title: 'Refunds',
    icon: 'ArrowUturnLeftIcon',
    items: [
      { question: 'What is the refund policy?', answer: 'Due to the digital nature of our products, refunds are generally not offered once a template has been downloaded. However, if you experience a technical issue that cannot be resolved, please contact support.' },
      { question: 'How do I request a refund?', answer: 'Submit a support ticket through your account dashboard explaining the issue. Our team will review your request within 2-3 business days.' },
      { question: 'What qualifies for a refund?', answer: 'Refunds may be considered for: templates that are significantly different from their description, templates with critical bugs that cannot be fixed, or duplicate purchases.' },
    ],
  },
  {
    title: 'Support',
    icon: 'LifebuoyIcon',
    items: [
      { question: 'How do I get support?', answer: 'Logged-in customers can submit a support ticket from their account dashboard. Our team typically responds within 24-48 hours on business days.' },
      { question: 'What is the difference between Support Tickets and the Community?', answer: 'Support Tickets are private, direct communications with the ZyForge team for account-specific issues. The Community is a public forum for peer-to-peer help, tips, and discussions.' },
      { question: 'Can I get help with template customization?', answer: 'Basic installation support is included. For advanced customization, visit the Community where experienced members share tips and tricks.' },
      { question: 'What are your support hours?', answer: 'Our support team is available Monday–Friday, 9am–6pm EST. We aim to respond to all tickets within 24 business hours.' },
    ],
  },
];

export default function PublicFAQClient() {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  const toggle = (key: string) => {
    setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="min-h-screen pt-20 pb-16" style={{ background: 'var(--bg-primary)' }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ background: 'var(--accent-light)' }}
          >
            <Icon name="QuestionMarkCircleIcon" size={28} style={{ color: 'var(--accent)' }} />
          </div>
          <h1 className="text-3xl font-display font-bold mb-3" style={{ color: 'var(--text-primary)' }}>Frequently Asked Questions</h1>
          <p className="text-base" style={{ color: 'var(--text-secondary)' }}>
            Everything you need to know about ZyForge Marketplace.
          </p>
          <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
            Can't find your answer?{' '}
            <Link href="/support" style={{ color: 'var(--accent)' }} className="hover:underline">Submit a support ticket</Link>
            {' '}or visit the{' '}
            <Link href="/community" style={{ color: 'var(--accent)' }} className="hover:underline">Community</Link>.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {FAQ_SECTIONS.map((section) => (
            <div key={section.title}>
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: 'var(--accent-light)' }}
                >
                  <Icon name={section.icon} size={16} style={{ color: 'var(--accent)' }} />
                </div>
                <h2 className="font-display font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>{section.title}</h2>
              </div>
              <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
                {section.items.map((item, idx) => {
                  const key = `${section.title}-${idx}`;
                  const isOpen = openItems[key];
                  return (
                    <div key={key} className="border-b last:border-0" style={{ borderColor: 'var(--border)' }}>
                      <button
                        onClick={() => toggle(key)}
                        className="w-full flex items-center justify-between px-5 py-4 text-left transition-colors hover:opacity-80"
                        style={{ background: 'var(--bg-card)' }}
                      >
                        <span className="font-medium text-sm pr-4" style={{ color: 'var(--text-primary)' }}>{item.question}</span>
                        <Icon
                          name={isOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                          size={16}
                          style={{ color: 'var(--text-secondary)', flexShrink: 0 }}
                        />
                      </button>
                      {isOpen && (
                        <div
                          className="px-5 pb-4 text-sm leading-relaxed"
                          style={{ background: 'var(--bg-card)', color: 'var(--text-secondary)' }}
                        >
                          {item.answer}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
